import styled from "styled-components";
import {
  ProductState,
  FeatureType,
  SelectedProductFeatureMapState,
  ProductFeatureState,
  GlobalStyleState,
} from "../store/chart";
import { ReactComponent as CheckIcon } from "../assets/images/check.svg";
import { Text } from "./Text";
import { FontWeight } from "../types";

type ComparisonProps = {
  product: ProductState;
  productFeatureMap?: SelectedProductFeatureMapState;
  legalCopyArray: string[];
  styling: GlobalStyleState;
};

type ComparisonPlaceholderProps = {
  product: ProductState;
  styling: GlobalStyleState;
  children?: React.ReactNode;
};

export function Comparison({
  product,
  productFeatureMap,
  legalCopyArray,
  styling,
}: ComparisonProps) {
  if (product.isBundle) {
    const productFeature1 =
      productFeatureMap?.productFeatureMap[product.leftData.id];
    const productFeature2 =
      productFeatureMap?.productFeatureMap[product.rightData.id];
    const sharedColumn = shoundBundleShareColumn(
      productFeature1,
      productFeature2,
    );

    return (
      <Container
        columnBackgroundColor={styling.column.backgroundColor?.hex}
        secondaryColumnBackgroundColor={
          product.isBundle ? styling.column.bundleBackgroundColor?.hex : ""
        }
      >
        {sharedColumn ? (
          <Column>
            <ProductFeatureComparison
              productFeature={productFeature1}
              legalCopyArray={legalCopyArray}
            />
          </Column>
        ) : (
          <>
            <HalfColumn>
              <ProductFeatureComparison
                productFeature={productFeature1}
                legalCopyArray={legalCopyArray}
              />
            </HalfColumn>
            <HalfColumn>
              <ProductFeatureComparison
                productFeature={productFeature2}
                legalCopyArray={legalCopyArray}
              />
            </HalfColumn>
          </>
        )}
      </Container>
    );
  }

  const productFeature = productFeatureMap?.productFeatureMap[product.id];

  return (
    <Container columnBackgroundColor={styling.column.backgroundColor?.hex}>
      <Column>
        <ProductFeatureComparison
          productFeature={productFeature}
          legalCopyArray={legalCopyArray}
        />
      </Column>
    </Container>
  );
}

export function ComparisonPlaceholder({
  product,
  styling,
  children,
}: ComparisonPlaceholderProps) {
  return (
    <Container
      columnBackgroundColor={styling.column.backgroundColor?.hex}
      secondaryColumnBackgroundColor={
        product.isBundle ? styling.column.bundleBackgroundColor?.hex : ""
      }
    >
      {children}
    </Container>
  );
}

type ProductFeatureComparisonProps = {
  productFeature?: ProductFeatureState;
  legalCopyArray: string[];
};

function ProductFeatureComparison({
  productFeature,
  legalCopyArray,
}: ProductFeatureComparisonProps) {
  if (!productFeature || productFeature.type === FeatureType.BLANK) {
    return <div />;
  }

  if (productFeature.type === FeatureType.CHECKED) {
    return (
      <Checked>
        <CheckIcon />
      </Checked>
    );
  }

  let legalCopy;
  if (productFeature.itemLegalCopy) {
    const index = legalCopyArray.indexOf(productFeature.itemLegalCopy);
    legalCopy = index >= 0 ? <sup>{index + 1}</sup> : null;
  }

  if (productFeature.type === FeatureType.ICON) {
    return (
      <ImageWrapper>
        <img src={productFeature.icon.url} alt={productFeature.icon.alt} />
        {legalCopy}
      </ImageWrapper>
    );
  }

  return (
    <ItemWrapper>
      {productFeature.item && (
        <Item>
          {productFeature.item.includes("*") ? (
            <>
              {productFeature.item.split("*")[0]}
              {legalCopy}
              {productFeature.item.split("*")[1]}
            </>
          ) : (
            <>
              {productFeature.item}
              {legalCopy}
            </>
          )}
        </Item>
      )}
      {productFeature.subItem && <SubItem>{productFeature.subItem}</SubItem>}
    </ItemWrapper>
  );
}

type ProductOverviewComparisonProps = {
  overview?: string;
};

export function ProductOverviewComparison({
  overview,
}: ProductOverviewComparisonProps) {
  return (
    <ItemWrapper>
      <SubItem>{overview}</SubItem>
    </ItemWrapper>
  );
}

function shoundBundleShareColumn(
  productFeature1: ProductFeatureState | undefined,
  productFeature2: ProductFeatureState | undefined,
) {
  if (!productFeature1 && !productFeature2) {
    return true;
  }
  if (
    (productFeature1 && !productFeature2) ||
    (!productFeature1 && productFeature2)
  ) {
    return false;
  }
  if (
    productFeature1?.type === FeatureType.SUB_ITEM &&
    productFeature2?.type === FeatureType.SUB_ITEM
  ) {
    return productFeature1.featureItemId === productFeature2.featureItemId;
  }
  return false;
}

const Container = styled.div<{
  columnBackgroundColor?: string;
  secondaryColumnBackgroundColor?: string;
}>`
  position: relative;
  z-index: 1;
  display: flex;
  height: 100%;
  background: ${({ theme, columnBackgroundColor = theme.colors.white }) =>
    columnBackgroundColor};

  &:before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 50%;
    width: 50%;
    height: 100%;
    content: "";
    border-radius: 0;
    background: ${({ secondaryColumnBackgroundColor }) =>
      secondaryColumnBackgroundColor ? secondaryColumnBackgroundColor : "none"};
  }
`;

const Column = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const HalfColumn = styled(Column)`
  width: 50%;
`;

const Checked = styled.div`
  svg {
    width: ${({ theme }) =>
      theme.isMobile ? theme.getValue(15) : theme.getValue(26)}px;
    height: ${({ theme }) =>
      theme.isMobile ? theme.getValue(13) : theme.getValue(20)}px;
  }
`;

const ImageWrapper = styled.div`
  display: inline-flex;
  align-items: flex-start;
  padding: ${({ theme }) => `${theme.getValue(8)}px`};

  img {
    display: flex;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: ${({ theme }) =>
      theme.isMobile ? theme.getValue(40) : theme.getValue(45)}px;
    margin: 0 auto;
  }

  sup {
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(10) : theme.getFontSize(12)}px;
    margin-top: 8px;
  }
`;

const ItemWrapper = styled.div`
  padding: ${({ theme }) => `${theme.getValue(24)}px ${theme.getValue(8)}px`};
`;

const Item = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(16) : theme.getFontSize(22)}px;
    line-height: 1;
    margin: 0;
    text-align: center;
    color: ${({ theme, textColor }) => textColor?.hex || theme.colors.green1};
    hyphens: ${({ theme }) => (theme.isMobile ? "auto" : "none")};

    sup {
      font-size: ${({ theme }) =>
        theme.isMobile ? theme.getFontSize(10) : theme.getFontSize(12)}px;
      position: relative;
      top: -4px;
    }
  }
`;

const SubItem = styled(Text)`
  &&& {
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(16) : theme.getFontSize(22)}px;
    line-height: 1;
    margin: 0;
    text-align: center;
    hyphens: ${({ theme }) => (theme.isMobile ? "auto" : "none")};
  }
`;
